import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, X, UserPlus } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { createProject, updateProject } from '@/lib/api/projects';
import { lookupUserByEmail } from '@/lib/api/users';
import { useAuth } from '@/hooks/useAuth';
import type {
  DealType,
  ProjectCreateRequest,
  ProjectUpdateRequest,
  ProjectMemberRole,
  ProjectWithRFIResponse,
} from '@/types/backend';
import { CompanyAutocompleteInput } from '@/components/rfi/CompanyAutocompleteInput';
import { JurisdictionMultiSelect } from '@/components/rfi/JurisdictionMultiSelect';

const ROLE_DESCRIPTIONS: Record<ProjectMemberRole, string> = {
  viewer: 'Can view project content',
  editor: 'Can view and edit project content',
  manager: 'Full access including team management',
};

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

interface CreateProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  project?: ProjectWithRFIResponse;
}

const dealTypeToSide = (dt?: DealType | null): 'buy-side' | 'sell-side' | '' => {
  if (dt === 'buy_side') return 'buy-side';
  if (dt === 'sell_side') return 'sell-side';
  return '';
};

export function CreateProjectDialog({ open, onOpenChange, onSuccess, project }: CreateProjectDialogProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const currentUserEmail = user?.email?.toLowerCase() ?? null;
  const isEditMode = !!project;
  // In create mode the creator becomes manager automatically; in edit mode only managers can change membership.
  const canManageTeam = !isEditMode || project?.current_user_role === 'manager';
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    projectName: project?.name ?? '',
    entityName: project?.entity_name ?? '',
    jurisdictions: project?.jurisdictions ?? [] as string[],
    transactionSide: dealTypeToSide(project?.deal_type),
  });
  const [teamMembers, setTeamMembers] = useState<{ email: string; role: ProjectMemberRole }[]>([]);
  const [memberEmail, setMemberEmail] = useState('');
  const [memberRole, setMemberRole] = useState<ProjectMemberRole>('editor');
  const [isLookingUpMember, setIsLookingUpMember] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFormData({
      projectName: project?.name ?? '',
      entityName: project?.entity_name ?? '',
      jurisdictions: project?.jurisdictions ?? [],
      transactionSide: dealTypeToSide(project?.deal_type),
    });
    setTeamMembers(
      project?.members?.map((m) => ({ email: m.email, role: m.role })) ?? [],
    );
    setMemberEmail('');
  }, [open, project]);

  const handleAddMember = async () => {
    const email = memberEmail.trim().toLowerCase();
    if (!email) return;
    if (!isValidEmail(email)) {
      toast({ title: 'Invalid email', description: 'Please enter a valid email address.', variant: 'destructive' });
      return;
    }
    if (teamMembers.some((m) => m.email === email)) {
      toast({ title: 'Already added', description: `${email} is already invited.`, variant: 'destructive' });
      return;
    }

    setIsLookingUpMember(true);
    try {
      const user = await lookupUserByEmail(email);
      if (!user) {
        toast({
          title: 'User not found',
          description: `${email} is not registered. Ask them to sign up first.`,
          variant: 'destructive',
        });
        return;
      }
      setTeamMembers((prev) => [...prev, { email: user.email, role: memberRole }]);
      setMemberEmail('');
    } catch (error) {
      console.error('Failed to look up user:', error);
      toast({
        title: 'Lookup failed',
        description: 'Could not validate the email. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLookingUpMember(false);
    }
  };

  const handleRemoveMember = (email: string) => {
    setTeamMembers((prev) => prev.filter((m) => m.email !== email));
  };

  const handleMemberRoleChange = (email: string, role: ProjectMemberRole) => {
    setTeamMembers((prev) => prev.map((m) => (m.email === email ? { ...m, role } : m)));
  };

  const handleInputChange = (field: string, value: string | string[]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleJurisdictionsChange = (value: string[]) => {
    setFormData((prev) => ({ ...prev, jurisdictions: value }));
  };

  const handleSubmit = async () => {
    if (!formData.projectName.trim()) {
      toast({ title: 'Validation Error', description: 'Please enter a project name.', variant: 'destructive' });
      return;
    }
    if (!formData.entityName.trim()) {
      toast({ title: 'Validation Error', description: 'Please enter an entity name.', variant: 'destructive' });
      return;
    }
    if (formData.jurisdictions.length === 0) {
      toast({ title: 'Validation Error', description: 'Please select at least one jurisdiction.', variant: 'destructive' });
      return;
    }
    if (!formData.transactionSide) {
      toast({ title: 'Validation Error', description: 'Please select a transaction side.', variant: 'destructive' });
      return;
    }

    setIsCreating(true);

    try {
      const dealType: DealType = formData.transactionSide === 'buy-side' ? 'buy_side' : 'sell_side';

      if (isEditMode && project) {
        const updateRequest: ProjectUpdateRequest = {
          name: formData.projectName,
          entity_name: formData.entityName,
          jurisdictions: formData.jurisdictions,
          deal_type: dealType,
          ...(teamMembers.length > 0 ? { members: teamMembers } : {}),
        };
        await updateProject(project.id, updateRequest);
        toast({ title: 'Project updated', description: `"${formData.projectName}" has been updated.` });
        onOpenChange(false);
        onSuccess?.();
        return;
      }

      const createRequest: ProjectCreateRequest = {
        name: formData.projectName,
        entity_name: formData.entityName,
        jurisdictions: formData.jurisdictions,
        deal_type: dealType,
        ...(teamMembers.length > 0 ? { members: teamMembers } : {}),
      };

      const created = await createProject(createRequest);

      setFormData({
        projectName: '',
        entityName: '',
        jurisdictions: [],
        transactionSide: '',
      });
      setTeamMembers([]);
      setMemberEmail('');

      onOpenChange(false);
      onSuccess?.();
      navigate(`/project/${created.id}`);
    } catch (error) {
      console.error(isEditMode ? 'Failed to update project:' : 'Failed to create project:', error);
      toast({
        title: 'Error',
        description: isEditMode ? 'Failed to update project. Please try again.' : 'Failed to create project. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Edit Project' : 'Create New Project'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="projectName" className="text-foreground font-medium">
              Project name
            </Label>
            <Input
              id="projectName"
              placeholder="e.g., Project Alpha"
              value={formData.projectName}
              onChange={(e) => handleInputChange('projectName', e.target.value)}
              disabled={isCreating}
              className="bg-background/50"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="entityName" className="text-foreground font-medium">
              Entity name
            </Label>
            <CompanyAutocompleteInput
              placeholder="e.g., Acme Holdings Ltd"
              value={formData.entityName}
              onValueChange={(v) => handleInputChange('entityName', v)}
              disabled={isCreating}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="jurisdictions" className="text-foreground font-medium">
              Jurisdiction(s)
            </Label>
            <JurisdictionMultiSelect
              value={formData.jurisdictions}
              onValueChange={handleJurisdictionsChange}
              disabled={isCreating}
              placeholder="Type to search countries"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-foreground font-medium">
              Transaction type
            </Label>
            <div className="flex gap-3">
              <Button
                type="button"
                variant={formData.transactionSide === 'buy-side' ? 'default' : 'outline'}
                className={`flex-1 ${formData.transactionSide === 'buy-side' ? 'btn-premium' : ''}`}
                onClick={() => handleInputChange('transactionSide', 'buy-side')}
                disabled={isCreating}
              >
                Buy-side
              </Button>
              <Button
                type="button"
                variant={formData.transactionSide === 'sell-side' ? 'default' : 'outline'}
                className={`flex-1 ${formData.transactionSide === 'sell-side' ? 'btn-premium' : ''}`}
                onClick={() => handleInputChange('transactionSide', 'sell-side')}
                disabled={isCreating}
              >
                Sell-side
              </Button>
            </div>
          </div>

          {canManageTeam && (
          <div className="space-y-2">
            <div className="flex items-baseline justify-between">
              <Label className="text-foreground font-medium">Team members</Label>
              <span className="text-xs text-muted-foreground">Optional</span>
            </div>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="colleague@firm.com"
                  value={memberEmail}
                  onChange={(e) => setMemberEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddMember();
                    }
                  }}
                  disabled={isCreating || isLookingUpMember}
                  className="bg-background/50 flex-1"
                />
                <select
                  value={memberRole}
                  onChange={(e) => setMemberRole(e.target.value as ProjectMemberRole)}
                  disabled={isCreating || isLookingUpMember}
                  aria-label="Role"
                  className="h-10 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                >
                  <option value="viewer">Viewer</option>
                  <option value="editor">Editor</option>
                  <option value="manager">Manager</option>
                </select>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAddMember}
                  disabled={isCreating || isLookingUpMember || !memberEmail.trim()}
                  className="gap-1.5 shrink-0"
                >
                  {isLookingUpMember ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                  Add
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">{ROLE_DESCRIPTIONS[memberRole]}</p>

            {teamMembers.length > 0 && (
              <ul className="mt-2 space-y-1.5 rounded-md border border-border/60 bg-background/40 p-2">
                {teamMembers.map((member) => {
                  const isSelf = currentUserEmail !== null && member.email.toLowerCase() === currentUserEmail;
                  return (
                    <li
                      key={member.email}
                      className="flex items-center gap-2 rounded-sm px-2 py-1.5 hover:bg-muted/40"
                    >
                      <span className="flex-1 truncate text-sm text-foreground">
                        {member.email}
                        {isSelf && <span className="text-muted-foreground ml-1">(you)</span>}
                      </span>
                      {isSelf ? (
                        <span className="rounded border border-border/60 bg-muted px-2 py-0.5 text-xs text-muted-foreground capitalize">
                          {member.role}
                        </span>
                      ) : (
                        <select
                          value={member.role}
                          onChange={(e) => handleMemberRoleChange(member.email, e.target.value as ProjectMemberRole)}
                          disabled={isCreating}
                          aria-label={`Role for ${member.email}`}
                          className="rounded border border-border/60 bg-background px-2 py-0.5 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                        >
                          <option value="viewer">Viewer</option>
                          <option value="editor">Editor</option>
                          <option value="manager">Manager</option>
                        </select>
                      )}
                      {!isSelf && (
                        <button
                          type="button"
                          onClick={() => handleRemoveMember(member.email)}
                          disabled={isCreating}
                          aria-label={`Remove ${member.email}`}
                          className="rounded p-1 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          )}

        </div>

        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isCreating}
            className="gap-2"
          >
            {isCreating && <Loader2 className="w-4 h-4 animate-spin" />}
            {isEditMode ? (isCreating ? 'Saving...' : 'Save changes') : 'Create Project'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
